import { z } from "zod";
/**
 * Crash reports from the app (plan D48, T42).
 *
 * The app's own small reporter — no third-party SDK — catches `window.onerror`,
 * `unhandledrejection` and a React error boundary, and `sendBeacon`s one
 * report here. The route only LOGS it (Workers Logs); nothing is stored, so a
 * report is never a personal-data store with its own deletion duty (repo
 * D153).
 *
 * §13 of the plan's `api-contracts.md`, verbatim:
 *
 * ```ts
 * // POST /client-errors   (auth optional; if a valid bearer is present, log the accountId)
 * clientErrorInput = z.strictObject({
 *   source: z.enum(["error", "unhandledrejection", "boundary"]),
 *   message: z.string().max(2000),
 *   stack: z.string().max(8000).optional(),
 *   bundleVersion: z.string().min(1).max(64),
 *   platform: z.enum(["android", "web"]),
 *   route: z.string().max(500).optional(),     // location.search, which carries the screen state
 *   occurredAt: z.iso.datetime(),
 * });
 * // → 204. Body > 16 KiB → 413. Rate limit on the ip and fingerprint axes
 * // (identityMiddleware first, T-21). Writes only to the log (ConsoleLogger), never to D1.
 * ```
 *
 * ── THE BODY MAY ARRIVE AS `text/plain` ─────────────────────────────────────
 * `navigator.sendBeacon(url, string)` sends `text/plain;charset=UTF-8`, a
 * CORS-safelisted type that needs no preflight — and a beacon CANNOT perform a
 * preflight, so a JSON-typed Blob from another origin is dropped by the
 * browser before it leaves. The server parses both `text/plain` and
 * `application/json` as JSON.
 */
export const CLIENT_ERRORS_ROUTE = "/client-errors";
/** The whole request body may not exceed 16 KiB (413 `client_errors.too_large`). */
export const CLIENT_ERRORS_MAX_BYTES = 16 * 1024;
/** The per-field caps, named so the client truncates to the same numbers. */
export const CLIENT_ERROR_LIMITS = Object.freeze({
    bundleVersion: 64,
    message: 2000,
    route: 500,
    stack: 8000,
});
export const clientErrorSource = z.enum([
    "error",
    "unhandledrejection",
    "boundary",
]);
export const clientErrorPlatform = z.enum(["android", "web"]);
/** Strict: an unknown field is a 400, never silently dropped. The schema
 *  plus the 16 KiB cap is the whole abuse surface of an unauthenticated POST. */
export const clientErrorInput = z.strictObject({
    source: clientErrorSource,
    message: z.string().max(CLIENT_ERROR_LIMITS.message),
    stack: z.string().max(CLIENT_ERROR_LIMITS.stack).optional(),
    bundleVersion: z.string().min(1).max(CLIENT_ERROR_LIMITS.bundleVersion),
    platform: clientErrorPlatform,
    // location.search, which carries the screen state
    route: z.string().max(CLIENT_ERROR_LIMITS.route).optional(),
    occurredAt: z.iso.datetime(),
});
//# sourceMappingURL=client-errors.js.map